import React, { useEffect, useState } from 'react';
import { Alert, Box, CircularProgress, Collapse, IconButton, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RoundedPaper from 'shared/components/RoundedPaper';
import AgentHubService from 'services/AgentHubService';
import { fmtTime } from './parts';
import { AgentUsage, HubPlan, PlanWindow, UsageReport } from './types';

const service = new AgentHubService();
type Period = 'today' | 'd7' | 'd30';
const PERIODS: Record<Period, string> = { today: 'Today', d7: '7 days', d30: '30 days' };
type Sort = 'tokens' | 'runs' | 'avgTokens';

const usd = (x: number) => `$${x < 10 ? x.toFixed(2) : Math.round(x).toLocaleString()}`;
/** 12.4M / 830k / 512 */
const tok = (x: number) => (x >= 1e9 ? `${(x / 1e9).toFixed(1)}B` : x >= 1e6 ? `${(x / 1e6).toFixed(1)}M` : x >= 1e3 ? `${Math.round(x / 1e3)}k` : String(Math.round(x)));
/** A token figure; hovering shows its API-equivalent estimate (per run, and the total where it's a sum). */
function Tokens({ tokens, cost, runs, bold }: { tokens: number; cost: number; runs?: number; bold?: boolean }) {
  const title = runs && runs > 1 ? `Estimated ${usd(cost / runs)} per run (${usd(cost)} in all), API-equivalent` : `Estimated ${usd(cost)}${runs === 1 ? ' for this run' : ''}, API-equivalent`;
  return <Tooltip title={title} arrow><span style={{ fontWeight: bold ? 600 : undefined, cursor: 'help' }}>{tok(tokens)}</span></Tooltip>;
}
const MEASURE = 'Tokens are weighted by what they cost against the plan: every token of the run, its sub-agents included, with cache reads counted at 0.1, cache writes at 2 and output at 5 (relative to fresh input, as priced). Unweighted, cheap cache re-reads would swamp the figure.';

function AgentRow({ a, period, share, limit }: { a: AgentUsage; period: Period; share: number; limit: number | null }) {
  const [open, setOpen] = useState(false);
  const t = a[period];
  return (
    <>
      <TableRow hover onClick={() => setOpen(o => !o)} sx={{ cursor: 'pointer' }}>
        <TableCell padding="checkbox"><IconButton size="small">{open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}</IconButton></TableCell>
        <TableCell><RouterLink to={`/agents/${a.agent}`} onClick={e => e.stopPropagation()}>{a.agent}</RouterLink></TableCell>
        <TableCell align="right">{t.runs}</TableCell>
        <TableCell align="right"><Tokens tokens={t.tokens} cost={t.cost} runs={t.runs} bold /></TableCell>
        <TableCell align="right">{t.runs ? <Tokens tokens={t.avgTokens} cost={t.avgCost} runs={1} /> : '—'}</TableCell>
        <TableCell align="right">{`${(share * 100).toFixed(share < 0.1 ? 1 : 0)}%`}</TableCell>
        <TableCell align="right">{limit ? pctOf(a.d7.tokens / limit) : '—'}</TableCell>
        <TableCell align="right">{a.cacheReadShare == null ? '—' : `${Math.round(a.cacheReadShare * 100)}%`}</TableCell>
        <TableCell align="right" sx={{ color: a.trendPct != null && a.trendPct > 0.5 ? '#B91C1C' : undefined }}>{a.trendPct == null ? '—' : `${a.trendPct > 0 ? '+' : ''}${Math.round(a.trendPct * 100)}%`}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={9} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Heaviest runs, last 30 days</Typography>
              {a.topRuns.map(r => (
                <Typography key={r.runId} variant="body2" component="div">
                  <Tokens tokens={r.tokens} cost={r.cost} runs={1} /> · <RouterLink to={`/agents/${a.agent}?run=${encodeURIComponent(r.runId)}`}>{fmtTime(r.startedAt)}</RouterLink>
                  {r.subject ? ` · ${r.subject}` : ''}{r.minutes != null ? ` · ${Math.round(r.minutes)} min` : ''}{r.turns != null ? ` · ${r.turns} turns` : ''}{r.status !== 'done' ? ` · ${r.status}` : ''}
                </Typography>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

const pctOf = (x: number) => `${(x * 100).toFixed(x < 0.1 ? 1 : 0)}%`;
const pacific = (iso: string) => new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', weekday: 'short', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });
const LIMIT_HOW = "Anthropic doesn't publish the plan's limit in tokens. Every 6 hours the hub notes how full the weekly window is and how many tokens it has used; between two notes, the hub's tokens over how much the window filled gives the limit. Kevin's own Claude use fills the window too, so this is a worst case: the real limit is likely higher, and each share of it lower.";

const WEEK_MS = 7 * 24 * 3_600_000;

/**
 * The weekly window at its current pace (Kevin, 2026-10-23: a simple projection): its fill so far over the share of
 * the week gone. Null in the first 6 hours of a window (too little to go on) or without a reset time.
 */
export function weekProjection(w: PlanWindow | null | undefined, at: string): { used: number; elapsed: number; projected: number; fullAt: string | null } | null {
  if (!w?.resetsAt) return null;
  const reset = Date.parse(w.resetsAt);
  const start = reset - WEEK_MS;
  const elapsed = (Date.parse(at) - start) / WEEK_MS;
  if (elapsed < 6 / (7 * 24) || elapsed > 1) return null;
  const projected = w.utilization / elapsed;
  // at this pace, when the window would be full (only when that's before the reset)
  const fullAt = projected > 1 ? new Date(start + (elapsed / w.utilization) * WEEK_MS).toISOString() : null;
  return { used: w.utilization, elapsed, projected, fullAt };
}

/** The plan: the estimated weekly limit, the hub's share of it, and how full each window is now. */
export function PlanPanel({ plan, week }: { plan: HubPlan; week: number }) {
  const e = plan.estimate;
  const w = plan.windows;
  const win = (label: string, x: { utilization: number; resetsAt: string | null } | null | undefined) => (x ? `${label} ${Math.round(x.utilization * 100)}% used${x.resetsAt ? `, resets ${pacific(x.resetsAt)}` : ''}` : null);
  return (
    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
      <RoundedPaper sx={{ px: 2, py: 1.5, minWidth: 260 }}>
        <Typography variant="caption" color="text.secondary"><Tooltip title={LIMIT_HOW} arrow><span style={{ cursor: 'help' }}>Weekly limit (estimate, worst case)</span></Tooltip></Typography>
        {e ? (
          <>
            <Typography variant="h6">{tok(e.weeklyLimit)} tokens</Typography>
            <Typography variant="caption" color="text.secondary" component="div">About {tok(e.weeklyLimit * 4.3)} a month. Range {tok(e.low)} to {tok(e.high)}, from {e.pairs} snapshot pair(s); includes Kevin's own Claude use.</Typography>
          </>
        ) : <Typography variant="body2" sx={{ mt: 0.5 }}>Not estimated yet: it needs two snapshots, 6 hours apart, while the weekly window fills.</Typography>}
      </RoundedPaper>
      {e && (
        <RoundedPaper sx={{ px: 2, py: 1.5, minWidth: 200 }}>
          <Typography variant="caption" color="text.secondary">The hub's last 7 days</Typography>
          <Typography variant="h6">{pctOf(week / e.weeklyLimit)} of the limit</Typography>
          <Typography variant="caption" color="text.secondary">{tok(week)} tokens</Typography>
        </RoundedPaper>
      )}
      {w && (
        <RoundedPaper sx={{ px: 2, py: 1.5, minWidth: 260 }}>
          <Typography variant="caption" color="text.secondary">The plan now (all use, as of {pacific(w.at)})</Typography>
          {[win('5-hour window:', w.fiveHour), win('Weekly window:', w.sevenDay)].filter(Boolean).map(x => <Typography key={x} variant="body2">{x}</Typography>)}
        </RoundedPaper>
      )}
      {w && (() => {
        const p = weekProjection(w.sevenDay, w.at);
        return p && (
          <RoundedPaper sx={{ px: 2, py: 1.5, minWidth: 220 }}>
            <Typography variant="caption" color="text.secondary"><Tooltip title="All use of the subscription this week, the hub's and Kevin's, at the pace so far: the share used over the share of the week gone." arrow><span style={{ cursor: 'help' }}>This week, at this pace</span></Tooltip></Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}><b>{pctOf(p.used)}</b> used, with <b>{pctOf(p.elapsed)}</b> of the week gone</Typography>
            {/* the meter: how full the window is; the mark is where an even pace would be by now */}
            <Box sx={{ position: 'relative', height: 8, my: 1, borderRadius: 4, bgcolor: 'action.hover' }} aria-label={`${pctOf(p.used)} used, ${pctOf(p.elapsed)} of the week gone`}>
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.min(100, p.used * 100)}%`, borderRadius: 4, bgcolor: p.used > p.elapsed ? 'warning.main' : 'primary.main' }} />
              <Box sx={{ position: 'absolute', top: -3, bottom: -3, left: `${p.elapsed * 100}%`, width: 2, bgcolor: 'text.primary' }} />
            </Box>
            <Typography variant="h6" color={p.projected > 1 ? 'error.main' : p.projected > 0.85 ? 'warning.main' : undefined}>{pctOf(p.projected)} by the reset</Typography>
            <Typography variant="caption" color="text.secondary" component="div">{p.fullAt ? `At this pace it's full around ${pacific(p.fullAt)}` : `Resets ${pacific(w.sevenDay!.resetsAt!)}`}</Typography>
          </RoundedPaper>
        );
      })()}
    </Box>
  );
}

/** What each agent consumed, in weighted tokens (MEASURE); the API-equivalent estimate is on hover. */
export default function UsageTab() {
  const [report, setReport] = useState<UsageReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<Period>('d30');
  const [sort, setSort] = useState<Sort>('tokens');
  const [plan, setPlan] = useState<HubPlan | null>(null);
  useEffect(() => { service.usage().then(setReport).catch(e => setError(e.message)); }, []);
  // The plan comes with the hub's live state: a missing one only hides the panel (the hub is older or down).
  useEffect(() => { service.agents().then(r => setPlan(r.hub?.state.plan ?? null)).catch(() => setPlan(null)); }, []);
  if (error) return <Alert severity="error">Usage didn't load: {error}</Alert>;
  if (!report) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;
  const agents = [...report.agents].sort((x, y) => y[period][sort] - x[period][sort]);
  // % of budget: each agent's share of everything the hub's agents used in the period (scheduled ones and the PR
  // pipeline's, which the pipeline starts): the column adds up to 100%.
  const all = report.agents.reduce((s, a) => s + a[period].tokens, 0);
  const head = (s: Sort, label: string) => <TableCell align="right"><TableSortLabel active={sort === s} direction="desc" onClick={() => setSort(s)}>{label}</TableSortLabel></TableCell>;
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {MEASURE} Hover a figure for its estimated cost at API prices; the hub runs on the Claude subscription, so nothing is billed per run. As of {fmtTime(report.at)}.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        {(Object.keys(PERIODS) as Period[]).map(p => (
          <RoundedPaper key={p} sx={{ px: 2, py: 1.5, minWidth: 160 }}>
            <Typography variant="caption" color="text.secondary">{PERIODS[p]}</Typography>
            <Typography variant="h6"><Tokens tokens={report.totals[p].tokens} cost={report.totals[p].cost} runs={report.totals[p].runs} /> tokens</Typography>
            <Typography variant="caption" color="text.secondary">{report.totals[p].runs} runs</Typography>
          </RoundedPaper>
        ))}
      </Box>
      {plan && <PlanPanel plan={plan} week={report.totals.d7.tokens} />}
      {plan?.estimate && report.totals.d7.tokens / plan.estimate.weeklyLimit > 0.7 && (
        <Alert severity="warning" icon={false} sx={{ mb: 2 }}>The hub alone used {pctOf(report.totals.d7.tokens / plan.estimate.weeklyLimit)} of the estimated weekly limit in the last 7 days (over 70%): Kevin's own Claude use and the hub may run into the limit.</Alert>
      )}
      {report.hints.length > 0 && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }}>
          <b>Where to look</b>
          {report.hints.map(h => <div key={h}>{h}</div>)}
        </Alert>
      )}
      <RoundedPaper sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.5 }}>
          <Typography variant="subtitle1">By agent</Typography>
          <ToggleButtonGroup size="small" exclusive value={period} onChange={(_e, v) => v && setPeriod(v)}>
            {(Object.keys(PERIODS) as Period[]).map(p => <ToggleButton key={p} value={p}>{PERIODS[p]}</ToggleButton>)}
          </ToggleButtonGroup>
        </Box>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>Agent</TableCell>
              {head('runs', 'Runs')}
              {head('tokens', 'Tokens')}
              {head('avgTokens', 'Per run')}
              <TableCell align="right"><Tooltip title="Its share of all the hub's agents used in this period (scheduled agents and the PR pipeline's): the column adds up to 100%." arrow><span>% of budget</span></Tooltip></TableCell>
              <TableCell align="right"><Tooltip title={`Its last 7 days' tokens as a share of the estimated weekly limit (a worst case: ${LIMIT_HOW})`} arrow><span>% of weekly limit</span></Tooltip></TableCell>
              <TableCell align="right">Input from cache (30d)</TableCell>
              <TableCell align="right">Per run, week over week</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{agents.map(a => <AgentRow key={a.agent} a={a} period={period} share={all ? a[period].tokens / all : 0} limit={plan?.estimate?.weeklyLimit ?? null} />)}</TableBody>
        </Table>
      </RoundedPaper>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <RoundedPaper sx={{ flex: '1 1 320px' }}>
          <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5 }}>Per PR, by repo (30 days: prep, review, merge, deploy check, /ask)</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>Repo</TableCell><TableCell align="right">PRs</TableCell><TableCell align="right">Per PR</TableCell><TableCell align="right">Total</TableCell></TableRow></TableHead>
            <TableBody>{report.repos.map(r => <TableRow key={r.repo}><TableCell>{r.repo}</TableCell><TableCell align="right">{r.prs}</TableCell><TableCell align="right"><Tokens tokens={r.perPr} cost={r.costPerPr} bold /></TableCell><TableCell align="right"><Tokens tokens={r.tokens} cost={r.cost} /></TableCell></TableRow>)}</TableBody>
          </Table>
        </RoundedPaper>
        <RoundedPaper sx={{ flex: '2 1 480px' }}>
          <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5 }}>Heaviest PRs (30 days)</Typography>
          <Table size="small">
            <TableHead><TableRow><TableCell>PR</TableCell><TableCell align="right">Runs</TableCell><TableCell align="right">Tokens</TableCell><TableCell>By agent</TableCell></TableRow></TableHead>
            <TableBody>
              {report.prs.slice(0, 15).map(p => (
                <TableRow key={p.subject}>
                  <TableCell>{p.subject}</TableCell>
                  <TableCell align="right">{p.runs}</TableCell>
                  <TableCell align="right"><Tokens tokens={p.tokens} cost={p.cost} runs={p.runs} bold /></TableCell>
                  <TableCell>{Object.entries(p.byAgent).sort((a, b) => b[1] - a[1]).map(([a, t]) => `${a} ${tok(t)}`).join(' · ')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </RoundedPaper>
      </Box>
    </>
  );
}
